import { CommonModule } from '@angular/common';
import {
  Component,
  ElementRef,
  HostListener,
  Input,
  OnInit,
  ViewChild,
} from '@angular/core';
import { FormArray, FormControl, FormGroup, Validators } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { MatTabGroup, MatTabsModule } from '@angular/material/tabs';
import { IClient } from '@techspert-io/clients';
import { SpinnerComponent } from '@techspert-io/design-system';
import { IOpportunity } from '@techspert-io/opportunities';
import { ToastService } from '@techspert-io/user-alerts';
import {
  catchError,
  concatMap,
  debounceTime,
  filter,
  map,
  Observable,
  of,
  skip,
  startWith,
  tap,
} from 'rxjs';
import {
  CognisearchCondition,
  IField,
  ISearchCondition,
  ISearchConditionWindow,
  ISearchJobHistoryCondition,
  ISearchQuery,
} from '../../../models/query';
import {
  ISearchDisplayExpert,
  ISearchStats,
  ISelectOpportunityDialogData,
} from '../../../models/search-models';
import { SearchService } from '../../../services/search.service';
import {
  CORE_TARGET_WARNING,
  CORE_TARGET_WARNING_THRESHOLD,
  getTargetMaxForCoreNetwork,
} from '../../cognisearch-target.constants';
import { ResultsDisplayBetaComponent } from '../results-display/results-display.component';
import { SearchQueryFormComponent } from '../search-query-form/search-query-form.component';
import {
  SearchConditionOperator,
  SearchJobHistoryConditionsForm,
  SearchQueryConditionsFields,
  SearchQueryConditionsForm,
  SearchQueryConditionTimings,
  SearchQueryCoreNetworkOptions,
  SearchQueryForm,
  SearchQueryWindowType,
} from '../search-query-form/search-query-form.models';
import { SelectOpportunityDialogComponent } from '../select-opportunity-dialog/select-opportunity-dialog.component';

interface IDisplayQuery {
  query: ISearchQuery;
  validationErrors: string[];
  validationWarnings: string[];
}

@Component({
  selector: 'app-search-layout',
  templateUrl: './search-layout.component.html',
  styleUrls: ['./search-layout.component.scss'],
  imports: [
    CommonModule,
    MatTabsModule,
    SearchQueryFormComponent,
    ResultsDisplayBetaComponent,
    SpinnerComponent,
  ],
})
export class SearchLayoutComponent implements OnInit {
  @ViewChild('UploadFileInput') uploadFileInputRef: ElementRef;
  @ViewChild('tabs') tabGroup: MatTabGroup;

  @Input() opportunity?: IOpportunity;
  @Input() client?: IClient;

  searchQuery$: Observable<IDisplayQuery>;
  queryStats$: Observable<ISearchStats | { error: string }>;

  form: SearchQueryForm;
  maskedMode = false;
  sampleExperts?: ISearchDisplayExpert[];
  sampleLoading = false;

  get nameConditions(): FormArray {
    return this.form.get('nameConditions') as FormArray;
  }
  get workConditions(): FormArray {
    return this.form.get('workConditions') as FormArray;
  }
  get knowledgeConditions(): FormArray {
    return this.form.get('knowledgeConditions') as FormArray;
  }
  get jobHistoryConditions(): FormArray {
    return this.form.get('jobHistoryConditions') as FormArray;
  }

  constructor(
    public dialog: MatDialog,
    private searchService: SearchService,
    private toastService: ToastService
  ) {}

  ngOnInit(): void {
    this.form = new FormGroup({
      coreNetwork: new FormControl<SearchQueryCoreNetworkOptions>('core', [
        Validators.required,
      ]),
      target: new FormControl<number>(null, [
        Validators.required,
        Validators.min(1),
        Validators.max(getTargetMaxForCoreNetwork('core')),
      ]),
      countries: new FormControl<string[]>([], [Validators.required]),
      subdivisions: new FormControl<string[]>([]),
      nameConditions: new FormArray<SearchQueryConditionsForm>([]),
      workConditions: new FormArray<SearchQueryConditionsForm>([]),
      knowledgeConditions: new FormArray<SearchQueryConditionsForm>([]),
      jobHistoryConditions: new FormArray<SearchJobHistoryConditionsForm>([]),
      honoraria: new FormGroup({
        min: new FormControl<number | null>(null),
        max: new FormControl<number | null>(null),
        currency: new FormControl<string | null>(null),
      }),
      phoneNumber: new FormControl<boolean>(false),
    });

    this.searchQuery$ = this.form.valueChanges.pipe(
      map((value) => this.mapToSearchQuery(value)),
      map((query) => ({
        query,
        validationErrors: this.validationErrors(query),
        validationWarnings: this.validationWarnings(query),
      })),
      tap(() => (this.tabGroup.selectedIndex = 0)),
      tap(() => (this.sampleExperts = undefined)),
      startWith({
        query: this.mapToSearchQuery(this.form.value),
        validationErrors: [],
        validationWarnings: [],
      })
    );

    this.queryStats$ = this.searchQuery$.pipe(
      skip(1),
      debounceTime(2000),
      filter(
        ({ query, validationErrors }) =>
          !validationErrors ||
          !this.validationErrors({ ...query, count: 1 }).length
      ),
      concatMap(({ query: { count, ...rest } }) =>
        this.searchService.stats({ ...rest, service: 'cognisearch-stats' })
      ),
      catchError((error) => {
        console.error('Error fetching search query stats:', error);
        return of({ error: JSON.stringify(error) });
      })
    );
  }

  @HostListener('window:keypress', ['$event'])
  keyEvent(event: KeyboardEvent): void {
    if (event.key === '\u0013' && event.shiftKey && event.ctrlKey) {
      this.maskedMode = !this.maskedMode;
    }
  }

  runQuery({ query, validationErrors, validationWarnings }: IDisplayQuery) {
    if (this.form.pristine) {
      validationErrors.push(...this.validationErrors(query));
      validationWarnings.push(...this.validationWarnings(query));
    }

    if (this.sampleLoading) {
      return;
    }
    if (validationErrors.length) {
      this.toastService.sendMessage(
        `Search query has validation errors: ${validationErrors.join(', ')}`,
        'danger'
      );
      return;
    }
    if (validationWarnings.length) {
      const coreTargetWarning = this.getCoreTargetWarning(query);
      if (coreTargetWarning) {
        this.toastService.sendMessage(coreTargetWarning, 'error');
      }

      const otherWarnings = validationWarnings.filter(
        (warning) => warning !== coreTargetWarning
      );
      if (otherWarnings.length) {
        this.toastService.sendMessage(
          `Search query has validation warnings: ${otherWarnings.join(', ')}`,
          'error'
        );
      }
    }
    this.sampleLoading = true;

    this.searchService.search({ ...query, count: 300 }).subscribe({
      next: (response) => {
        this.sampleExperts = response.experts;
        this.tabGroup.selectedIndex = 1;
        this.sampleLoading = false;

        if (!response.experts.length) {
          this.toastService.sendMessage(
            'No experts available for search',
            'error'
          );
        }
      },
      error: (error) => {
        this.sampleLoading = false;
        console.error('Error running search query:', error);
        this.toastService.sendMessage(
          `Error running search query: ${JSON.stringify(error)}`,
          'danger'
        );
      },
    });
  }

  submitSearch({ query, validationErrors, validationWarnings }: IDisplayQuery) {
    if (!this.sampleExperts?.length) {
      this.toastService.sendMessage(
        'No experts available for search',
        'danger'
      );
      return;
    }
    if (validationErrors.length) {
      this.toastService.sendMessage(
        `Search query has validation errors: ${validationErrors.join(', ')}`,
        'danger'
      );
      return;
    }
    if (validationWarnings.length) {
      this.toastService.sendMessage(
        `Search query has validation warnings: ${validationWarnings.join(', ')}`,
        'error'
      );
      return;
    }

    const sendExpertsDialogData: ISelectOpportunityDialogData = {
      experts: [],
      selectedCount: query.count,
      dialogMode: this.opportunity ? true : false,
      query: query,
      opportunity: this.opportunity,
      client: this.client,
    };

    this.dialog.open<
      SelectOpportunityDialogComponent,
      ISelectOpportunityDialogData
    >(SelectOpportunityDialogComponent, {
      data: sendExpertsDialogData,
      width: '600px',
      height: 'max-content',
    });
  }

  downloadQuery() {
    const date = new Date().toISOString().slice(0, 10);

    const dataStr = `data:text/json;charset=utf-8,${encodeURIComponent(
      JSON.stringify(this.mapToSearchQuery(this.form.value), null, 2)
    )}`;
    const dlAnchorElem = document.getElementById('downloadAnchorElem');
    dlAnchorElem.setAttribute('href', dataStr);
    dlAnchorElem.setAttribute('download', `search-query-${date}.json`);
    dlAnchorElem.click();
  }

  uploadQuery() {
    this.uploadFileInputRef.nativeElement.click();
  }

  onUploadQuery(target: HTMLInputElement) {
    const file = target.files[0];
    const reader = new FileReader();
    reader.readAsText(file);
    reader.onload = (): void => {
      const result = JSON.parse(reader.result as string) as ISearchQuery;
      this.setFormForSearchQuery(result);
    };
  }

  private mapToSearchQuery(value: SearchQueryForm['value']): ISearchQuery {
    const conditionFieldMap: Record<
      SearchQueryConditionsFields,
      Record<SearchQueryConditionTimings, IField['fieldValue']>
    > = {
      names: {
        all: 'name',
        past: 'name',
        'past-advanced': 'name',
        current: 'name',
      },
      roles: {
        all: 'role',
        past: 'past_role',
        'past-advanced': 'past_role',
        current: 'current_role',
      },
      affiliations: {
        all: 'affiliation',
        past: 'past_affiliation',
        'past-advanced': 'past_affiliation',
        current: 'current_affiliation',
      },
      workSettings: {
        all: 'work_setting',
        past: 'work_setting',
        'past-advanced': 'work_setting',
        current: 'work_setting',
      },
      industries: {
        all: 'industry',
        past: 'industry',
        'past-advanced': 'industry',
        current: 'current_industry',
      },
      expertise: {
        all: 'expertise',
        past: 'expertise',
        'past-advanced': 'expertise',
        current: 'expertise',
      },
      publications: {
        all: 'expertise',
        past: 'expertise',
        'past-advanced': 'expertise',
        current: 'expertise',
      },
      boardCertifications: {
        all: 'expertise',
        past: 'expertise',
        'past-advanced': 'expertise',
        current: 'expertise',
      },
      clinicalTrials: {
        all: 'expertise',
        past: 'expertise',
        'past-advanced': 'expertise',
        current: 'expertise',
      },
      treatments: {
        all: 'treatment',
        past: 'treatment',
        current: 'treatment',
        'past-advanced': 'treatment',
      },
      medicalConditions: {
        all: 'condition',
        past: 'condition',
        current: 'condition',
        'past-advanced': 'treatment',
      },
    };
    const conditionOperatorMap: Record<string, string> = {
      include: 'or',
      exclude: 'exclude',
    };
    const jobHistoryFieldMap: Record<
      SearchQueryConditionTimings,
      ISearchJobHistoryCondition['field']
    > = {
      all: 'job_history',
      current: 'current_job_history',
      past: 'past_job_history',
      'past-advanced': 'past_job_history',
    };

    return {
      service: 'cognisearch',
      count: +value.target,
      locations: [
        ...(value.subdivisions || []).map((s) => ({
          country: 'US',
          subdivision: s,
        })),
        ...(value.countries || [])
          .filter((c) => c !== 'US' || !value.subdivisions?.length)
          .map((c) => ({ country: c })),
      ],
      coreNetwork: value.coreNetwork,
      conditions: [
        ...[
          ...value.nameConditions,
          ...value.knowledgeConditions,
          ...value.workConditions,
        ].map((condition) => ({
          field: conditionFieldMap[condition.field][condition.time],
          operator: conditionOperatorMap[condition.operator],
          terms: condition.terms,
          ...this.getSearchConditionWindow(condition),
        })),
        ...value.jobHistoryConditions.map((condition) => ({
          field: jobHistoryFieldMap[condition.time],
          roles: condition.roles,
          affiliations: condition.affiliations,
          operator: 'or',
          ...this.getSearchConditionWindow(condition),
        })),
      ],
      phoneNumber: value.phoneNumber ? 'required' : 'optional',
      searchExpansion: true,
    };
  }

  private validationErrors(query: ISearchQuery) {
    const validationErrors: string[] = [];
    const targetMax = getTargetMaxForCoreNetwork(query.coreNetwork);

    if (!query.count || query.count < 1 || query.count > targetMax) {
      validationErrors.push(
        `Count must be between 1 and ${targetMax.toLocaleString()}.`
      );
    }
    if (!query.locations.length) {
      validationErrors.push('At least one location is required.');
    }
    if (!query.conditions.length) {
      validationErrors.push('At least one condition is required.');
    }
    const fields = ['terms', 'roles', 'affiliations'];
    if (
      query.conditions.some((c) => fields.some((f) => f in c && !c[f].length))
    ) {
      validationErrors.push('Cannot have empty conditions.');
    }
    if (
      query.conditions.some(
        (c) =>
          'numMonths' in c &&
          (isNaN(c.numMonths) ||
            c.numMonths < 0 ||
            !Number.isInteger(c.numMonths))
      )
    ) {
      validationErrors.push(
        'Where selected, months must be a non-negative whole number.'
      );
    }

    return validationErrors;
  }

  private validationWarnings(query: ISearchQuery) {
    const warnings: string[] = [];

    if (
      !query.conditions.some(
        (c) =>
          c.field !== 'expertise' && (!('terms' in c) || c.operator === 'or')
      )
    ) {
      warnings.push(
        'Searches without a non-expertise include condition are for information only and cannot be added to an opportunity.'
      );
    }
    const coreTargetWarning = this.getCoreTargetWarning(query);
    if (coreTargetWarning) {
      warnings.push(coreTargetWarning);
    }
    return warnings;
  }

  private getCoreTargetWarning(query: ISearchQuery): string | undefined {
    if (
      query.coreNetwork === 'core' &&
      query.count > CORE_TARGET_WARNING_THRESHOLD &&
      query.count <= getTargetMaxForCoreNetwork('core')
    ) {
      return CORE_TARGET_WARNING;
    }

    return undefined;
  }

  private setFormForSearchQuery(query: ISearchQuery) {
    interface IMappedField {
      field: SearchQueryConditionsFields;
      time: SearchQueryConditionTimings;
    }
    const reverseConditionFieldMap: Record<IField['fieldValue'], IMappedField> =
      {
        name: { field: 'names', time: 'all' },
        role: { field: 'roles', time: 'all' },
        past_role: { field: 'roles', time: 'past' },
        current_role: { field: 'roles', time: 'current' },
        affiliation: { field: 'affiliations', time: 'all' },
        past_affiliation: { field: 'affiliations', time: 'past' },
        current_affiliation: { field: 'affiliations', time: 'current' },
        work_setting: { field: 'workSettings', time: 'all' },
        industry: { field: 'industries', time: 'all' },
        current_industry: { field: 'industries', time: 'current' },
        expertise: { field: 'expertise', time: 'all' },
        treatment: { field: 'treatments', time: 'all' },
        condition: { field: 'medicalConditions', time: 'all' },
        // below not used, just for types
        position: {
          field: 'expertise',
          time: 'all',
        },
        job_history: {
          field: 'expertise',
          time: 'all',
        },
        current_job_history: {
          field: 'expertise',
          time: 'all',
        },
        past_job_history: {
          field: 'expertise',
          time: 'all',
        },
      };

    const jobHistoryFieldMap: Record<
      ISearchJobHistoryCondition['field'],
      SearchQueryConditionTimings
    > = {
      job_history: 'all',
      current_job_history: 'current',
      past_job_history: 'past',
    };
    const jobHistoryFields = Object.keys(jobHistoryFieldMap);

    const jobHistoryConditions = query.conditions.flatMap((c) => {
      if (jobHistoryFields.includes(c.field)) {
        const condition = c as ISearchJobHistoryCondition;

        const { time, ...window } =
          this.mapCongisearchConditionWindowToFormWindow(condition);

        return [
          {
            ...condition,
            ...window,
            time: time || jobHistoryFieldMap[condition.field] || 'all',
          },
        ];
      }

      return [];
    });

    const otherConditions: {
      field: SearchQueryConditionsFields;
      operator: 'include' | 'exclude';
      terms: string[];
      time: SearchQueryConditionTimings;
      windowType: SearchQueryWindowType;
      windowMonths: number;
    }[] = query.conditions
      .filter((c) => !jobHistoryFields.includes(c.field))
      .map((c: ISearchCondition) => {
        const mappedField: IMappedField = reverseConditionFieldMap[c.field];
        const { time, ...window } =
          this.mapCongisearchConditionWindowToFormWindow(c);

        return {
          field: mappedField.field,
          operator: c.operator === 'or' ? 'include' : 'exclude',
          terms: c.terms?.filter(Boolean) || [],
          time: time || mappedField.time,
          ...window,
        };
      });

    for (const control of [
      this.nameConditions,
      this.workConditions,
      this.knowledgeConditions,
    ]) {
      while (control.length) {
        control.removeAt(0);
      }
    }

    for (const condition of jobHistoryConditions) {
      this.jobHistoryConditions.push(
        new FormGroup({
          roles: new FormControl<string[]>(condition.roles || []),
          affiliations: new FormControl<string[]>(condition.affiliations || []),
          operator: new FormControl<SearchConditionOperator>('include', [
            Validators.required,
          ]),
          time: new FormControl<SearchQueryConditionTimings>(condition.time, [
            Validators.required,
          ]),
          windowType: new FormControl<SearchQueryWindowType>(null),
          windowMonths: new FormControl<number | null>(
            condition.windowMonths || null
          ),
        })
      );
    }

    const fieldToControlMap: Record<
      SearchQueryConditionsFields,
      FormArray<SearchQueryConditionsForm>
    > = {
      names: this.nameConditions,
      roles: this.workConditions,
      affiliations: this.workConditions,
      workSettings: this.workConditions,
      industries: this.workConditions,
      expertise: this.knowledgeConditions,
      publications: this.knowledgeConditions,
      boardCertifications: this.knowledgeConditions,
      clinicalTrials: this.knowledgeConditions,
      treatments: this.knowledgeConditions,
      medicalConditions: this.knowledgeConditions,
    };

    for (const condition of otherConditions) {
      const controlArray = fieldToControlMap[condition.field];
      if (controlArray) {
        controlArray.push(
          new FormGroup({
            field: new FormControl<SearchQueryConditionsFields>(
              condition.field
            ),
            terms: new FormControl<string[]>(condition.terms, [
              Validators.required,
            ]),
            operator: new FormControl<SearchConditionOperator>(
              condition.operator
            ),
            time: new FormControl<SearchQueryConditionTimings>(condition.time, [
              Validators.required,
            ]),
            windowType: new FormControl<SearchQueryWindowType>(null),
            windowMonths: new FormControl<number>(condition.windowMonths),
          })
        );
      }
    }

    this.form.patchValue({
      coreNetwork: query.coreNetwork,
      target: query.count,
      countries: query.locations.map((l) => l.country),
      subdivisions: query.locations
        .filter((l) => l.subdivision)
        .map((l) => l.subdivision),
      phoneNumber: query.phoneNumber === 'required',
    });
  }

  private mapCongisearchConditionWindowToFormWindow(c: CognisearchCondition): {
    time?: SearchQueryConditionTimings;
    windowType: SearchQueryWindowType;
    windowMonths: number;
  } {
    const windowType = 'windowType' in c ? c.windowType : null;
    const windowMonths = 'numMonths' in c ? c.numMonths : null;

    return {
      time: windowType ? 'past-advanced' : undefined,
      windowType:
        typeof windowType === 'string' && windowType === 'no-more-than'
          ? windowType
          : 'at-least',
      windowMonths:
        typeof windowMonths === 'number' && !isNaN(windowMonths)
          ? windowMonths
          : 0,
    };
  }

  private getSearchConditionWindow({
    time,
    windowMonths,
    windowType,
  }: {
    time?: SearchQueryConditionTimings;
    windowMonths?: number;
    windowType?: SearchQueryWindowType;
  }): ISearchConditionWindow | undefined {
    if (!windowType || isNaN(windowMonths)) {
      return;
    }

    if (time === 'past-advanced') {
      return {
        numMonths: windowMonths,
        windowType,
      };
    }
  }
}
